import React, { useState, useEffect, useMemo } from "react";
import { 
  Boxes, 
  CheckCircle2, 
  Clock, 
  Bookmark, 
  AlertCircle,
  RefreshCw,
  Tag,
  PackageCheck,
  Factory
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { useCatalogContext } from "../../contexts/CatalogContext";
import { getBaseSku, cleanSku } from "../../utils/skuUtils";

/**
 * SecaoVariacoesEstoque.js — Lista Variações e Estoque da Ficha Canônica (P1.3)
 * 
 * Contrato intocado com inventoryService/PocketBase.
 * Agrupa todas as variações (aros/tamanhos) do SKU Pai e exibe o estoque em pronta-entrega, em fabricação e reservado.
 */
export default function SecaoVariacoesEstoque({ skuPai }) {
  const { catalog } = useCatalogContext();
  const [stockItems, setStockItems] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  // 1. Busca itens de estoque e reservas do PocketBase
  useEffect(() => {
    let active = true;
    const fetchStockData = async () => {
      if (!skuPai) return;
      setLoading(true);
      try {
        const [stockRes, resRes] = await Promise.all([
          pb.collection("inventory_items").getFullList({
            sort: "-created"
          }),
          pb.collection("reservations").getFullList({
            filter: `status = "PENDENTE"`,
            sort: "-created"
          })
        ]);

        if (active) {
          setStockItems(stockRes || []);
          setReservations(resRes || []);
        }
      } catch (err) {
        console.error("Erro ao carregar estoque das variações:", err);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchStockData();
    return () => { active = false; };
  }, [skuPai]);

  // 2. Mapeia e descobre todas as variações filhas pertencentes ao SKU Pai
  const variationsData = useMemo(() => {
    if (!skuPai) return { list: [], totals: { physical: 0, production: 0, reserved: 0 } };

    const variationSkusSet = new Set();

    // A. Varre o catálogo para encontrar SKUs cujo getBaseSku(sku) === skuPai
    if (catalog) {
      Object.values(catalog).forEach(item => {
        const itemSku = cleanSku(item.sku || item.full_data?.sku);
        if (itemSku && getBaseSku(itemSku) === skuPai) {
          variationSkusSet.add(itemSku);
        }
      });
    }

    // B. Varre itens de estoque existentes para capturar variações físicas
    stockItems.forEach(item => {
      const itemSku = cleanSku(item.sku || item.baseSku);
      if (itemSku && getBaseSku(itemSku) === skuPai) {
        variationSkusSet.add(itemSku);
      }
    });

    // C. Se nenhuma variação com sufixo foi encontrada, inclui o próprio SKU pai
    if (variationSkusSet.size === 0) {
      variationSkusSet.add(skuPai);
    }

    // Ordenação natural de SKUs (ex: aros numéricos 12, 14, 16...)
    const sortedSkus = Array.from(variationSkusSet).sort((a, b) => {
      const partsA = a.split("-");
      const partsB = b.split("-");
      const numA = parseInt(partsA[partsA.length - 1], 10);
      const numB = parseInt(partsB[partsB.length - 1], 10);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.localeCompare(b);
    });

    // Calcula estoque para cada variação
    let totalPhysical = 0;
    let totalProduction = 0;
    let totalReserved = 0;

    const list = sortedSkus.map(vSku => {
      // Itens físicos em pronta-entrega (in_stock & !isPE)
      const physicalQty = stockItems.filter(i => 
        cleanSku(i.sku) === vSku && 
        i.status === "in_stock" && 
        !i.isPE
      ).length;

      // Itens em produção / fábrica (isPE == true & não finalizados)
      const productionQty = stockItems.filter(i => 
        cleanSku(i.sku) === vSku && 
        i.isPE && 
        !["in_stock", "sold", "adjusted_out"].includes(i.status)
      ).length;

      // Reservas ativas
      const reservedQty = reservations.filter(r => 
        cleanSku(r.sku) === vSku
      ).reduce((sum, r) => sum + (Number(r.quantity) || 1), 0);

      totalPhysical += physicalQty;
      totalProduction += productionQty;
      totalReserved += reservedQty;

      // Extrai rótulo amigável de tamanho (ex: "Aro 14" ou "Tamanho Padrão")
      const parts = vSku.split("-");
      const lastPart = parts[parts.length - 1];
      const isNumericSize = /^\d+$/.test(lastPart);
      const sizeLabel = isNumericSize ? `Aro ${lastPart}` : (vSku === skuPai ? "Tamanho Único / Padrão" : `Variação ${lastPart}`);

      return {
        sku: vSku,
        sizeLabel,
        physicalQty,
        productionQty,
        reservedQty,
        catalogItem: catalog[vSku] || null
      };
    });

    return {
      list,
      totals: {
        physical: totalPhysical,
        production: totalProduction,
        reserved: totalReserved
      }
    };
  }, [skuPai, catalog, stockItems, reservations]);

  const { list: variationsList, totals } = variationsData;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6">
      
      {/* CABEÇALHO DA SEÇÃO */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
        <div className="flex items-center gap-2">
          <Boxes size={20} className="text-blue-600" />
          <div>
            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
              Variações e Estoque Físico (SKU Pai: {skuPai})
            </h3>
            <p className="text-xs text-slate-400">
              Mapeamento de aros, pronta-entrega e ordens de fabricação.
            </p>
          </div>
        </div>

        {/* RESUMO RÁPIDO DE ESTOQUE */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-semibold flex items-center gap-1.5">
            <PackageCheck size={14} />
            Pronta Entrega: <strong>{totals.physical} un</strong>
          </span>
          {totals.production > 0 && (
            <span className="px-3 py-1 bg-sky-50 text-sky-700 border border-sky-200 rounded-lg text-xs font-semibold flex items-center gap-1.5">
              <Factory size={14} />
              Em Produção: <strong>{totals.production} un</strong>
            </span>
          )}
          {totals.reserved > 0 && (
            <span className="px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-semibold flex items-center gap-1.5">
              <Bookmark size={14} />
              Reservas: <strong>{totals.reserved} un</strong>
            </span>
          )}
        </div>
      </div>

      {/* LISTAGEM DE VARIAÇÕES */}
      {loading ? (
        <div className="py-8 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
          <RefreshCw size={16} className="animate-spin text-blue-600" />
          <span>Consultando estoque das variações...</span>
        </div>
      ) : variationsList.length === 0 ? (
        <div className="py-8 text-center text-slate-400 space-y-1">
          <Boxes size={32} className="mx-auto stroke-1 opacity-40" />
          <p className="text-xs font-medium text-slate-500">Nenhuma variação ou item de estoque cadastrado para este SKU pai.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-600">
            <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-bold border-b border-slate-200">
              <tr>
                <th className="py-2.5 px-3">Medida / Tamanho</th>
                <th className="py-2.5 px-3">SKU da Variação</th>
                <th className="py-2.5 px-3 text-center">Pronta-Entrega</th>
                <th className="py-2.5 px-3 text-center">Em Fabricação</th>
                <th className="py-2.5 px-3 text-center">Reservado</th>
                <th className="py-2.5 px-3 text-right">Status do Estoque</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {variationsList.map((v) => {
                const hasStock = v.physicalQty > 0;
                const hasProduction = v.productionQty > 0;

                return (
                  <tr key={v.sku} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-3 font-bold text-slate-800 flex items-center gap-2">
                      <Tag size={13} className="text-slate-400" />
                      <span>{v.sizeLabel}</span>
                    </td>
                    <td className="py-3 px-3 font-mono text-slate-700">
                      {v.sku}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className={`px-2.5 py-1 rounded-md font-bold ${
                        v.physicalQty > 0 
                          ? "bg-emerald-100 text-emerald-800" 
                          : "bg-slate-100 text-slate-400"
                      }`}>
                        {v.physicalQty} un
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className={`px-2.5 py-1 rounded-md font-medium ${
                        v.productionQty > 0 
                          ? "bg-sky-100 text-sky-800 font-bold" 
                          : "text-slate-400"
                      }`}>
                        {v.productionQty > 0 ? `${v.productionQty} un` : "-"}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className={`px-2.5 py-1 rounded-md font-medium ${
                        v.reservedQty > 0 
                          ? "bg-amber-100 text-amber-800 font-bold" 
                          : "text-slate-400"
                      }`}>
                        {v.reservedQty > 0 ? `${v.reservedQty} un` : "-"}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      {hasStock ? (
                        <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md text-[11px] font-semibold inline-flex items-center gap-1">
                          <CheckCircle2 size={12} />
                          Pronta-Entrega
                        </span>
                      ) : hasProduction ? (
                        <span className="px-2.5 py-1 bg-sky-50 text-sky-700 border border-sky-200 rounded-md text-[11px] font-semibold inline-flex items-center gap-1">
                          <Clock size={12} />
                          Em Produção
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 bg-slate-100 text-slate-500 rounded-md text-[11px] font-medium inline-flex items-center gap-1">
                          <AlertCircle size={12} />
                          Sob Encomenda
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
